import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { ConnectPortalLayout } from "@/components/layout/ConnectPortalLayout";
import { CountryDetailContent, type CountryDetailFAQ } from "@/components/connect/CountryDetailContent";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { useCountryRates } from "@/hooks/useCountryRates";
import { useCallerCountry } from "@/hooks/useCallerCountry";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import type { ConnectCountryConfig } from "@/config/connect-countries";
import {
  CORRIDOR_DESTINATIONS,
  CORRIDOR_ORIGINS,
  alternatesForDestination,
  corridorForVisitor,
  corridorsForDestination,
  describeTimeDifference,
  type CorridorConfig,
} from "@/config/corridors";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";

interface ConnectCountryPageProps {
  country: ConnectCountryConfig;
  /** Set on /call/<destination>-from-<origin>: the page speaks to one origin market. */
  corridor?: CorridorConfig;
}

function billingNoteFor(corridor?: CorridorConfig): string {
  const base = "No hidden fees. Credit never expires. No subscription required.";
  if (!corridor) return `Rates shown in Australian dollars. ${base}`;
  const origin = CORRIDOR_ORIGINS[corridor.origin];
  if (origin.currency === "USD") return `Rates shown and billed in US dollars. ${base}`;
  if (origin.key === "australia") return `Rates shown in Australian dollars, GST included. ${base}`;
  return `Rates shown and billed in Australian dollars. ${base}`;
}

function corridorFaqs(country: ConnectCountryConfig, corridor: CorridorConfig): CountryDetailFAQ[] {
  const origin = CORRIDOR_ORIGINS[corridor.origin];
  const dest = CORRIDOR_DESTINATIONS[corridor.destination];
  const money =
    origin.currency === "USD"
      ? "Rates are shown and billed in US dollars."
      : origin.key === "australia"
        ? "Rates are shown in Australian dollars, GST included."
        : `Rates are shown and billed in Australian dollars, the currency elefa Connect bills ${origin.label} customers in today.`;
  const faqs: CountryDetailFAQ[] = [
    {
      question: `How much does it cost to call ${country.name} from ${origin.label}?`,
      answer: `${money} The live price list is on this page, and the rate for the number you dial appears before the call connects.`,
    },
    {
      question: `What is the country code for ${country.name}?`,
      answer: `The country code for ${country.name} is ${country.dialCode}. From ${origin.label}, dial ${country.dialCode} followed by the local number without its leading zero.`,
    },
  ];
  if (dest) {
    faqs.push(
      {
        question: `What time is it in ${dest.city} when I call from ${origin.city}?`,
        answer: describeTimeDifference(origin, dest),
      },
      {
        question: `Which networks in ${country.name} can I call?`,
        answer: `Any ${country.name} mobile or landline number, including ${dest.networks}. No app is needed on their side.`,
      },
    );
  }
  return faqs;
}

function CountryContent({
  country,
  corridor,
  isAuthenticated,
}: {
  country: ConnectCountryConfig;
  corridor?: CorridorConfig;
  isAuthenticated: boolean;
}) {
  const { t } = useTranslation("common");
  const origin = corridor ? CORRIDOR_ORIGINS[corridor.origin] : undefined;
  const currency = origin?.currency;
  const { all } = useCountryRates(currency ? { currency } : {});
  const dialCodeNoPlus = country.dialCode.replace(/^\+/, "");
  const matches = all.filter((r) => r.countryCode === dialCodeNoPlus);
  const landline = matches.find((m) => m.destinationType !== "mobile");
  const mobile = matches.find((m) => m.destinationType === "mobile");
  const primary = landline ?? matches[0];

  // Fallback to 0 if rates not yet loaded - UI will hide the rate gracefully
  const ratePerMinuteCents = primary?.ratePerMinuteCents ?? 0;
  const mobileRateCents = mobile?.ratePerMinuteCents;
  const story = country.storyKey
    ? t(country.storyKey, { defaultValue: country.story || "" })
    : country.story;

  return (
    <CountryDetailContent
      slug={country.slug}
      countryName={country.name}
      dialCode={dialCodeNoPlus}
      ratePerMinuteCents={ratePerMinuteCents}
      mobileRateCents={mobileRateCents}
      flagEmoji={country.flag}
      story={corridor ? corridor.intro : story || undefined}
      faqs={corridor ? corridorFaqs(country, corridor) : country.faqs}
      isAuthenticated={isAuthenticated}
      headline={origin ? `Call ${country.name} from ${origin.label}.` : undefined}
      billingNote={billingNoteFor(corridor)}
      currency={currency}
    />
  );
}

function OriginSwitcher({ country, corridor }: { country: ConnectCountryConfig; corridor?: CorridorConfig }) {
  const list = corridorsForDestination(country.slug);
  if (list.length === 0) return null;
  const linkClass = "underline underline-offset-4 hover:text-foreground";
  return (
    <section className="container py-6">
      <p className="text-sm text-muted-foreground text-center">
        Calling {country.name} from somewhere else?{" "}
        {list.map((c, i) => {
          const label = CORRIDOR_ORIGINS[c.origin].label;
          const current = corridor?.slug === c.slug;
          return (
            <span key={c.slug}>
              {i > 0 && " · "}
              {current ? (
                <span className="font-medium text-foreground">{label}</span>
              ) : (
                <Link to={`/call/${c.slug}`} className={linkClass}>{label}</Link>
              )}
            </span>
          );
        })}
        {corridor && (
          <>
            {" · "}
            <Link to={`/connect-portal/${country.slug}`} className={linkClass}>All {country.name} rates</Link>
          </>
        )}
      </p>
    </section>
  );
}

/** A suggestion, never a redirect: crawlers and visitors both get the page they asked for. */
function VisitorSuggestion({ country }: { country: ConnectCountryConfig }) {
  const { callerCountry } = useCallerCountry();
  const suggested = corridorForVisitor(country.slug, callerCountry);
  if (!suggested) return null;
  const origin = CORRIDOR_ORIGINS[suggested.origin];
  return (
    <div className="container pb-2">
      <Link
        to={`/call/${suggested.slug}`}
        className="block rounded-md bg-muted px-4 py-2 text-sm text-center hover:bg-muted/80"
      >
        Calling from {origin.prose}? See {country.name} rates for {origin.label} customers.
      </Link>
    </div>
  );
}

export function ConnectCountryPage({ country, corridor }: ConnectCountryPageProps) {
  const { t } = useTranslation("common");
  const { user, isLoading } = useAuth();
  const isAuthenticated = !!user;

  if (isLoading) {
    return <div className="min-h-[100dvh] bg-background" />;
  }

  if (isAuthenticated) {
    return (
      <ConnectPortalLayout requireAuth={false}>
        <CountryContent country={country} corridor={corridor} isAuthenticated={true} />
      </ConnectPortalLayout>
    );
  }

  const origin = corridor ? CORRIDOR_ORIGINS[corridor.origin] : undefined;
  const path = corridor ? `/call/${corridor.slug}` : `/connect-portal/${country.slug}`;
  const faqs = corridor ? corridorFaqs(country, corridor) : country.faqs;

  const faqSchema = faqs && faqs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  const breadcrumbSchema = getBreadcrumbSchema(
    origin
      ? [
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: `Call from ${origin.label}`, url: `https://elefa.tel/connect-portal/call-from-${origin.key}` },
          { name: country.name, url: `https://elefa.tel${path}` },
        ]
      : [
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: country.name, url: `https://elefa.tel/connect-portal/${country.slug}` },
        ],
  );

  const title = origin
    ? `Call ${country.name} from ${origin.prose} | elefa Connect`
    : `Call ${country.name} | elefa Connect`;
  const description = origin
    ? `Cheap calls to ${country.name} from ${origin.prose}. Rates in ${origin.currency === "USD" ? "US" : "Australian"} dollars, shown before you dial. Credit never expires, no contract.`
    : `Affordable HD calls to ${country.name}. Credit never expires. No contracts.`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={getCanonicalUrl(path)}
        structuredData={faqSchema ? [breadcrumbSchema, faqSchema] : breadcrumbSchema}
        alternates={alternatesForDestination(country.slug)}
      />
      <nav className="container py-6 flex items-center justify-between">
        <Link to="/connect-portal" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="outline" size="sm">{t("getStarted")}</Button>
          </Link>
        </div>
      </nav>

      {!corridor && <VisitorSuggestion country={country} />}
      <CountryContent country={country} corridor={corridor} isAuthenticated={false} />
      <OriginSwitcher country={country} corridor={corridor} />

      <ConnectFooter />
    </div>
  );
}
